import { Skeleton, Stack, type Theme, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';
import type { RVAdvisorInvitationApi } from '/@/hooks/nodes';
import { useDistributorsAndAdvisors } from '/@/hooks/nodes';
import { useIsValidatedRV } from '/@/hooks/session';

export const AdvisorDistributorNamesContext = createContext<Record<number, string>>({});

const useAdvisorDistributorNames = () => {
    const isRV = useIsValidatedRV();

    const distributorsAndAdvisorsQuery = useDistributorsAndAdvisors({}, { enabled: isRV });
    return useMemo(() => {
        const map: Record<number, string> = {};
        const results = distributorsAndAdvisorsQuery.data?.results ?? [];
        for (const el of results) {
            const distributorName = el.userNode.name;
            for (const child of el.children) {
                const existing = map[child.id];
                map[child.id] = existing ? `${existing}, ${distributorName}` : distributorName;
            }
        }
        return map;
    }, [distributorsAndAdvisorsQuery.data?.results]);
};

export const AdvisorDistributorNamesProvider = ({ children }: { children: ReactNode }) => {
    const advisorDistributorNames = useAdvisorDistributorNames();

    return (
        // @ts-expect-error React types compatibility issue with Context.Provider
        <AdvisorDistributorNamesContext.Provider value={advisorDistributorNames}>
            {children}
        </AdvisorDistributorNamesContext.Provider>
    );
};

export const SKELETON_KEYS = ['skeleton-0', 'skeleton-1', 'skeleton-2'] as const;

export type InvitationStatusEntry = {
    label: ReactNode;
    icon?: ReactNode;
    button?: ReactNode;
};

export type InvitationStatusConfig = Partial<
    Record<'pending' | 'declined' | 'accepted' | 'cancelled', InvitationStatusEntry>
>;

export const InvitationRow = ({
    invitation,
    index,
    statusConfig,
    overrideStatus,
}: {
    invitation: RVAdvisorInvitationApi;
    index: number;
    statusConfig: InvitationStatusConfig;
    overrideStatus?: 'pending' | 'accepted' | 'declined' | 'cancelled';
}) => {
    const advisorDistributorNames = useContext(AdvisorDistributorNamesContext);
    const distributorNames = advisorDistributorNames[invitation.advisor.id] ?? '';

    const effectiveStatus = overrideStatus ?? invitation.status;
    const config = effectiveStatus ? statusConfig[effectiveStatus as keyof InvitationStatusConfig] : undefined;

    const labelNode =
        config &&
        (typeof config.label === 'string' ? (
            <Typography variant='body1' color='primary' sx={{ whiteSpace: 'nowrap' }}>
                {config.label}
            </Typography>
        ) : (
            config.label
        ));

    return (
        <Stack
            p={2}
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            justifyContent={{ md: 'space-between' }}
            alignItems={{ md: 'center' }}
            sx={{
                border: (t: Theme) => `1px solid ${t.palette.divider}`,
                mt: index === 0 ? 0 : '-1px',
            }}
        >
            <Stack className='mp-mask' direction='column' justifyContent='flex-start' alignItems='flex-start'>
                <Typography variant='body1' color='primary' fontWeight='bold'>
                    {invitation.advisor.name}
                </Typography>
                <Typography variant='body1' color='primary'>
                    {distributorNames}
                </Typography>
            </Stack>
            {config && (
                <Stack direction='row' alignItems='center' spacing={1}>
                    {'icon' in config && config.icon}
                    <Stack direction='column' justifyContent='flex-start' alignItems='flex-start'>
                        {labelNode}
                        {'button' in config && config.button}
                    </Stack>
                </Stack>
            )}
        </Stack>
    );
};

export const InvitationListFrame = ({
    loading,
    invitations,
    emptyIcon,
    emptyMessage,
    children,
}: {
    loading: boolean;
    invitations: RVAdvisorInvitationApi[];
    emptyIcon: ReactNode;
    emptyMessage: string;
    children: ReactNode;
}) => {
    return (
        <AdvisorDistributorNamesProvider>
            <Stack sx={{ height: '300px', overflow: 'auto', width: '100%' }}>
                {loading ? (
                    SKELETON_KEYS.map((skeletonKey) => (
                        <Stack key={skeletonKey} p={1.5}>
                            <Skeleton variant='text' width='60%' height={24} />
                            <Skeleton variant='text' width='40%' height={20} />
                        </Stack>
                    ))
                ) : invitations.length === 0 ? (
                    <Stack alignItems='center' justifyContent='center' direction='column' spacing={3} height='100%'>
                        {emptyIcon}
                        <Typography variant='body1' color='primary'>
                            {emptyMessage}
                        </Typography>
                    </Stack>
                ) : (
                    children
                )}
            </Stack>
        </AdvisorDistributorNamesProvider>
    );
};
